import { Box, Spinner, Text } from '@chakra-ui/react';
import { OutlineCard, useToast } from '@morressier/alembic';
import {
  ISubmissionParticipantsResponse,
  SubmissionParticipantUser
} from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAllSubmissionParticipantsEmails,
  fetchDownloadEmailVerificationReport
} from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import ParticipantTable from './participantTable';
import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import Loading from 'src/pages/Status/Loading';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons';
import ProtectedActionButton from 'src/components/ProtectedAction/Button';

const EmailVerification = () => {
  const { workflowResponse } = useSubmissionData();
  const submissionId = workflowResponse?.submissionId;
  const submissionContextId = workflowResponse?.submissionContextId;
  const currentState = workflowResponse?.workflowData?.currentState;
  const { data: participantsEmailsData, isLoading } = useQuery({
    queryKey: ['participants-emails-data', { submissionId }],
    queryFn: () => fetchAllSubmissionParticipantsEmails(submissionId),
    retry: 0
  });
  const toast = useToast();
  const [downloadReportsLoading, setDownloadReportsLoading] =
    useState<boolean>(false);
  const DownloadReport = async () => {
    setDownloadReportsLoading(true);
    try {
      const ReportData = await fetchDownloadEmailVerificationReport(
        submissionId
      );
      const disposition = ReportData.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : 'report.pdf';
      const blob = new Blob([ReportData.data], {
        type: 'application/pdf'
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error While Downloading Report',
        description: 'Please try again later!',
        isClosable: true
      });
    } finally {
      setDownloadReportsLoading(false);
    }
  };
  if (isLoading) {
    return <Loading />;
  }
  if (!participantsEmailsData) {
    return (
      <NoRecordFound
        title="No Authors or Reviewers Found"
        subTitle="No Authors or Reviewers Found this submission yet."
      />
    );
  }

  const accordionItems = (
    Object.entries(participantsEmailsData) as [
      keyof ISubmissionParticipantsResponse,
      SubmissionParticipantUser[]
    ][]
  ).map(
    ([participantType, emails]) =>
      Boolean(emails.length) && (
        <Box key={participantType} mb={4}>
          <Text my={4} textStyle={'headingItem'} textTransform={'capitalize'}>
            {`${participantType} (${emails.length})`}
          </Text>
          <OutlineCard borderBottomWidth={0} borderRadius={0}>
            <Box>
              <ParticipantTable Participants={emails} />
            </Box>
          </OutlineCard>
        </Box>
      )
  );

  return (
    <>
      <Box display={'flex'} justifyContent={'end'}>
        <ProtectedActionButton
          relation="decision_maker"
          entityType="journal_phase"
          entityId={`${submissionContextId}_${currentState}`}
          size="context"
          variant="primary"
          as="text"
          textStyle={'textContext'}
          onClick={DownloadReport}
          disabled={downloadReportsLoading}
        >
          <FontAwesomeIcon icon={faCloudArrowDown} /> Download Report
          {downloadReportsLoading && <Spinner />}
        </ProtectedActionButton>
      </Box>
      <Box mt={2}>{accordionItems}</Box>
    </>
  );
};

export default EmailVerification;
